/**
 * The assembler's document: the rooms of a property, each carrying its own
 * whole-property placement (level / x / y / z / rotation) on its
 * `FloorPlanRoom`. Pure functions over the room list — structural sharing
 * throughout (an unchanged room keeps its reference), so committed-change
 * detection is reference identity, exactly like the room editor.
 */
import {
  DEFAULT_ROOM_LEVEL,
  type FloorPlanRoom,
  isRoomPlaced,
  type PlanBounds,
  roomAssemblyBounds,
  roomLevel,
  type RoomPlacement,
  roomPlacement,
  setRoomPlacement,
  unionPlanBounds,
} from '@kaiizen/kai-floor-plan';
import type { RoomNameStyle } from './roomLabels';

/** One room of the assembly. `id` is the host's key (the scan group id). */
export interface AssemblyRoom {
  id: string;
  /** The room's name as the plan shows it by default. */
  name: string;
  /** The room's name in each other style the host can give (see `RoomNameStyle`). */
  names?: Partial<Record<Exclude<RoomNameStyle, 'auto' | 'none'>, string>>;
  room: FloorPlanRoom;
}

/** Gap between auto-placed rooms, feet. */
export const AUTO_PLACE_GAP = 2;

/** How a level reads in the UI: 0 is the basement, 1 the first floor. */
export const levelLabel = (level: number): string => level === 0 ? 'Basement' : level < 0 ? `Basement ${level}` : `Level ${level}`;

/** Every level the floor plans are on, plus the default level, ascending. */
const floorPlanLevels = (floorPlans: readonly FloorPlanRoom[]): number[] =>
  [...new Set([DEFAULT_ROOM_LEVEL, ...floorPlans.map(roomLevel)])].sort((a, b) => a - b);

/**
 * The levels a room may be moved to: every level the floor plans are on,
 * plus one below and one above. The assembler passes every room's floor plan;
 * the room editor, which sees one room, passes that one.
 */
export function selectableLevels(floorPlans: readonly FloorPlanRoom[]): number[] {
  const levels = floorPlanLevels(floorPlans);
  return [levels[0] - 1, ...levels, levels[levels.length - 1] + 1];
}

export const findAssemblyRoom = (rooms: readonly AssemblyRoom[], id: string): AssemblyRoom | undefined => rooms.find(room => room.id === id);

export const roomsOnLevel = (rooms: readonly AssemblyRoom[], level: number): AssemblyRoom[] => rooms.filter(room => roomLevel(room.room) === level);

/** Every level any room is on, plus the default level, ascending. */
export const assemblyLevels = (rooms: readonly AssemblyRoom[]): number[] => floorPlanLevels(rooms.map(room => room.room));

/** The union of the rooms' assembly bounds — the measurement region; null with no rooms. */
export const assemblyBounds = (rooms: readonly AssemblyRoom[]): PlanBounds | null => unionPlanBounds(rooms.map(room => roomAssemblyBounds(room.room)));

/** Replace one room's floor plan via `update`; the same array when nothing changed. */
export function updateAssemblyRoom(rooms: AssemblyRoom[], id: string, update: (room: FloorPlanRoom) => FloorPlanRoom): AssemblyRoom[] {
  const index = rooms.findIndex(room => room.id === id);
  if (index < 0) return rooms;
  const current = rooms[index];
  const nextRoom = update(current.room);
  if (nextRoom === current.room) return rooms;
  const next = [...rooms];
  next[index] = { ...current, room: nextRoom };
  return next;
}

/** Patch one room's placement (any subset of level / x / y / z / rotation). */
export const placeAssemblyRoom = (rooms: AssemblyRoom[], id: string, patch: Partial<RoomPlacement>): AssemblyRoom[] =>
  updateAssemblyRoom(rooms, id, room => setRoomPlacement(room, patch));

/**
 * Do two versions of a room sit in the same place? Compares the effective
 * placement values AND whether the room is placed at all (an unplaced room is
 * never "the same" as a placed one at the origin).
 */
export function samePlacement(a: FloorPlanRoom, b: FloorPlanRoom): boolean {
  if (isRoomPlaced(a) !== isRoomPlaced(b)) return false;
  const pa = roomPlacement(a), pb = roomPlacement(b);
  return pa.level === pb.level && pa.x === pb.x && pa.y === pb.y && pa.z === pb.z && pa.rotation === pb.rotation;
}

/** `room` at `placed`'s whole-property placement, exactly as `placed` stores it; `room` itself when they already sit alike. */
export function withPlacementOf(room: FloorPlanRoom, placed: FloorPlanRoom): FloorPlanRoom {
  if (samePlacement(room, placed)) return room;
  const { level: _level, x: _x, y: _y, z: _z, rotation: _rotation, ...geometry } = room;
  const { level, x, y, z, rotation } = placed;
  return {
    ...geometry,
    ...(level !== undefined && { level }),
    ...(x !== undefined && { x }),
    ...(y !== undefined && { y }),
    ...(z !== undefined && { z }),
    ...(rotation !== undefined && { rotation }),
  };
}

/** The room's assembly bounds as if placed at the origin — its footprint size, rotation included. */
const footprintAtOrigin = (room: FloorPlanRoom): PlanBounds => roomAssemblyBounds({ ...room, x: 0, y: 0 });

/**
 * Give every unplaced room a position, level by level, so the assembler
 * always shows every room: beside the level's already-placed rooms when there
 * are any (one row to the right of their bounds), else in a compact grid from
 * the origin. Placing also pins the room's level explicitly. Deterministic in
 * room order; the same array comes back when every room was already placed.
 */
export function autoPlaceRooms(rooms: AssemblyRoom[]): AssemblyRoom[] {
  let next = rooms;
  for (const level of assemblyLevels(rooms)) {
    const onLevel = roomsOnLevel(rooms, level);
    const unplaced = onLevel.filter(room => !isRoomPlaced(room.room));
    if (!unplaced.length) continue;
    const placedBounds = assemblyBounds(onLevel.filter(room => isRoomPlaced(room.room)));
    const columns = placedBounds ? Infinity : Math.ceil(Math.sqrt(unplaced.length));
    const startX = placedBounds ? placedBounds.maxX + AUTO_PLACE_GAP : 0;
    const startY = placedBounds ? placedBounds.minY : 0;
    let cursorX = startX, cursorY = startY, rowHeight = 0, column = 0;
    for (const { id, room } of unplaced) {
      if (column >= columns) {
        column = 0;
        cursorX = startX;
        cursorY += rowHeight + AUTO_PLACE_GAP;
        rowHeight = 0;
      }
      const footprint = footprintAtOrigin(room);
      next = placeAssemblyRoom(next, id, { x: cursorX - footprint.minX, y: cursorY - footprint.minY, level });
      cursorX += footprint.maxX - footprint.minX + AUTO_PLACE_GAP;
      rowHeight = Math.max(rowHeight, footprint.maxY - footprint.minY);
      column++;
    }
  }
  return next;
}

/**
 * Every room back to the automatic initial placement: each room's position
 * and placement rotation are cleared, and `autoPlaceRooms` lays the rooms
 * out afresh, level by level. A room keeps its level, its floor elevation
 * and its own geometry. The same array comes back when the rooms already sit
 * as the reset would put them.
 */
export function resetAssemblyLayout(rooms: AssemblyRoom[]): AssemblyRoom[] {
  const unplaced = rooms.map(assemblyRoom => {
    const { x: _x, y: _y, rotation: _rotation, ...room } = assemblyRoom.room;
    return { ...assemblyRoom, room };
  });
  const reset = autoPlaceRooms(unplaced);
  return reset.every((assemblyRoom, index) => samePlacement(assemblyRoom.room, rooms[index].room)) ? rooms : reset;
}
